
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateEvent, useUpdateEvent } from '@/hooks/useEvents';
import { useBrands } from '@/hooks/useBrands';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useToast } from '@/hooks/use-toast';
import { Calendar, Clock, MapPin, DollarSign, Upload, FileText, X } from 'lucide-react';

interface EventFormProps {
  event?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const eventTypes = [
  'Product Launch',
  'Brand Activation',
  'Influencer Event',
  'Press Conference',
  'Workshop',
  'Networking',
  'Fashion Show',
  'Pop-up Store',
  'Conference',
  'Other'
];

const eventStatuses = [
  { value: 'draft', label: 'Draft' },
  { value: 'planned', label: 'Planned' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

export const EventForm: React.FC<EventFormProps> = ({ event, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: event?.name || '',
    event_type: event?.event_type || '',
    brand_id: event?.brand_id || '',
    campaign_id: event?.campaign_id || '',
    event_date: event?.event_date || '',
    event_time: event?.event_time || '',
    venue_address: event?.venue_address || '',
    total_budget: event?.total_budget || '',
    description: event?.description || '',
    status: event?.status || 'draft'
  });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const { data: brands } = useBrands();
  const { data: campaigns } = useCampaigns();
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.event_type || !formData.event_date) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const eventData = {
        ...formData,
        total_budget: formData.total_budget ? Number(formData.total_budget) : 0,
        brand_id: formData.brand_id || undefined,
        campaign_id: formData.campaign_id || undefined,
        event_time: formData.event_time || undefined,
        venue_address: formData.venue_address || undefined,
        description: formData.description || undefined
      };

      if (event) {
        await updateEvent.mutateAsync({ id: event.id, ...eventData });
        toast({
          title: "Success",
          description: "Event updated successfully"
        });
      } else {
        await createEvent.mutateAsync(eventData);
        toast({
          title: "Success",
          description: "Event created successfully"
        });
      }

      onSuccess?.();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save event",
        variant: "destructive"
      });
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files).filter(file => 
        file.size <= 10 * 1024 * 1024 // 10MB limit
      );
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-8 py-6">
      {/* Event Details Section */}
      <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-white to-gray-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <Calendar className="w-5 h-5 text-purple-600" />
            Event Details
          </CardTitle>
          <p className="text-sm text-gray-600">Basic information about your event</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-semibold text-gray-700">
                  Event Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter event name"
                  className="h-12 rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="event_type" className="text-sm font-semibold text-gray-700">
                  Event Type <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.event_type} onValueChange={(value) => handleChange('event_type', value)}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {eventTypes.map(type => (
                      <SelectItem key={type} value={type} className="rounded-lg">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="brand_id" className="text-sm font-semibold text-gray-700">Brand <span className="text-red-500">*</span></Label>
                <Select value={formData.brand_id} onValueChange={(value) => handleChange('brand_id', value)}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {brands?.map(brand => (
                      <SelectItem key={brand.id} value={brand.id} className="rounded-lg">{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign_id" className="text-sm font-semibold text-gray-700">Campaign</Label>
                <Select value={formData.campaign_id} onValueChange={(value) => handleChange('campaign_id', value)}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500">
                    <SelectValue placeholder="Select campaign" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {campaigns?.map(campaign => (
                      <SelectItem key={campaign.id} value={campaign.id} className="rounded-lg">{campaign.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="event_date" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-500" />
                  Event Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="event_date"
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => handleChange('event_date', e.target.value)}
                  className="h-12 rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="event_time" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-500" />
                  Event Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="event_time"
                  type="time"
                  value={formData.event_time}
                  onChange={(e) => handleChange('event_time', e.target.value)}
                  className="h-12 rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="total_budget" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  Total Budget <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="total_budget"
                  type="number"
                  value={formData.total_budget}
                  onChange={(e) => handleChange('total_budget', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  className="h-12 rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status" className="text-sm font-semibold text-gray-700">Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                  <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {eventStatuses.map(status => (
                      <SelectItem key={status.value} value={status.value} className="rounded-lg">{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue_address" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" />
                Venue Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="venue_address"
                value={formData.venue_address}
                onChange={(e) => handleChange('venue_address', e.target.value)}
                placeholder="Enter venue address"
                className="h-12 rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-semibold text-gray-700">Event Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe your event..."
                rows={4}
                className="rounded-xl border-gray-200 focus:ring-purple-500 focus:border-purple-500 resize-none"
              />
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Event Documents Section */}
      <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-white to-blue-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <Upload className="w-5 h-5 text-blue-600" />
            Event Documents
          </CardTitle>
          <p className="text-sm text-gray-600">Upload floor plans, presentations, and other event documents</p>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${
              isDragOver 
                ? 'border-purple-400 bg-purple-50' 
                : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Drop files here or click to upload</h3>
            <p className="text-sm text-gray-500 mb-4">PDF, DOC, DOCX, PNG, JPG up to 10MB each</p>
            <Button
              type="button"
              variant="outline"
              className="rounded-xl border-purple-200 text-purple-600 hover:bg-purple-50"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.multiple = true;
                input.accept = '.pdf,.doc,.docx,.png,.jpg,.jpeg';
                input.onchange = (e) => {
                  const target = e.target as HTMLInputElement;
                  handleFileUpload(target.files);
                };
                input.click();
              }}
            >
              <FileText className="w-4 h-4 mr-2" />
              Choose Files
            </Button>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="mt-6 space-y-2">
              <h4 className="text-sm font-semibold text-gray-700">Uploaded Files:</h4>
              {uploadedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">{file.name}</span>
                    <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          className="px-6 py-3 rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50"
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={createEvent.isPending || updateEvent.isPending}
          onClick={handleSubmit}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
        >
          {createEvent.isPending || updateEvent.isPending ? 'Saving...' : (event ? 'Update' : 'Create')} Event
        </Button>
      </div>
    </div>
  );
};
